/**
 * CookieConsentBanner.tsx
 *
 * GDPR-compliant cookie consent banner rendered at the bottom of the screen.
 * Shown once per browser session until the user makes a choice.
 * Choice is persisted in localStorage under the key `pulsehq_cookie_consent`.
 *
 * Three choices:
 *   accept_all    — analytics + functional + necessary cookies accepted
 *   accept_necessary — only strictly necessary cookies accepted
 *   manage        — opens the granular manage panel
 *
 * Granular panel allows toggling:
 *   - Functional cookies (user preferences, theme, language)
 *   - Analytics cookies (page-view tracking, feature usage)
 *   - Marketing cookies (lead attribution, retargeting) — always off for HRIS
 *
 * @module components/gdpr/CookieConsentBanner
 */

import React, { useState, useEffect } from 'react';
import { Cookie, Shield, ChevronDown, ChevronUp, X, Check } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CookieConsent {
  necessary: true;           // always true — cannot be opted out
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
  version: string;
}

type ConsentChoice = 'accept_all' | 'accept_necessary' | 'custom';

// ─── Constants ────────────────────────────────────────────────────────────────

const STORAGE_KEY   = 'pulsehq_cookie_consent';
const CONSENT_VER   = '1.0';

function buildConsent(functional: boolean, analytics: boolean, marketing: boolean): CookieConsent {
  return {
    necessary: true,
    functional,
    analytics,
    marketing,
    timestamp: new Date().toISOString(),
    version: CONSENT_VER,
  };
}

function loadStoredConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsent;
    // Re-show banner if consent version changed
    if (parsed.version !== CONSENT_VER) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveConsent(consent: CookieConsent): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch { /* storage unavailable — fail silently */ }
}

// ─── Toggle Component ─────────────────────────────────────────────────────────

interface ToggleProps {
  checked: boolean;
  disabled?: boolean;
  onChange: (val: boolean) => void;
  label: string;
  description: string;
}

function ConsentToggle({ checked, disabled = false, onChange, label, description }: ToggleProps) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-white/8 last:border-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        aria-pressed={checked}
        className={`relative flex-shrink-0 w-10 h-5 rounded-full transition-colors duration-200 focus:outline-none ${
          checked ? 'bg-teal-500' : 'bg-white/20'
        } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-white">{label}</p>
          {disabled && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-teal-500/20 text-teal-300 font-medium">
              Always on
            </span>
          )}
        </div>
        <p className="text-xs text-white/50 mt-0.5 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * CookieConsentBanner
 *
 * Renders nothing if the user has already made a consent choice.
 * Otherwise shows a slide-up banner at the bottom of the viewport.
 */
export default function CookieConsentBanner() {
  const [visible,     setVisible]     = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [functional,  setFunctional]  = useState(true);
  const [analytics,   setAnalytics]   = useState(false);
  const [marketing,   setMarketing]   = useState(false);

  useEffect(() => {
    const stored = loadStoredConsent();
    if (!stored) {
      // Small delay so the banner doesn't flash on first paint
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  if (!visible) return null;

  const handleChoice = (choice: ConsentChoice) => {
    let consent: CookieConsent;
    if (choice === 'accept_all') {
      consent = buildConsent(true, true, false); // marketing always off for HRIS
    } else if (choice === 'accept_necessary') {
      consent = buildConsent(false, false, false);
    } else {
      consent = buildConsent(functional, analytics, marketing);
    }
    saveConsent(consent);
    setVisible(false);
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] flex justify-center pb-4 px-4 pointer-events-none"
      aria-live="polite"
      aria-label="Cookie consent"
    >
      <div
        className="pointer-events-auto w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden"
        style={{
          background: 'rgba(16, 18, 30, 0.97)',
          border: '1px solid rgba(255,255,255,0.12)',
          backdropFilter: 'blur(20px)',
        }}
      >
        {/* Header */}
        <div className="flex items-start gap-3 p-4 pb-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-teal-500/20 flex items-center justify-center mt-0.5">
            <Cookie size={18} className="text-teal-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-bold text-white">We use cookies</h3>
              <span className="flex items-center gap-1 text-xs text-white/40">
                <Shield size={11} /> GDPR compliant
              </span>
            </div>
            <p className="text-xs text-white/60 leading-relaxed">
              We use cookies to improve your experience, remember your preferences, and analyse
              platform usage. You can customise your preferences or accept all cookies.{' '}
              <a
                href="/gdpr/privacy"
                className="text-teal-400 hover:text-teal-300 underline"
                onClick={e => e.stopPropagation()}
              >
                Privacy Policy
              </a>
            </p>
          </div>
          <button
            onClick={() => handleChoice('accept_necessary')}
            className="flex-shrink-0 text-white/30 hover:text-white/60 p-1 rounded-lg transition-colors"
            aria-label="Dismiss — accept necessary only"
          >
            <X size={16} />
          </button>
        </div>

        {/* Granular settings (collapsible) */}
        {showDetails && (
          <div
            className="mx-4 mb-3 rounded-xl p-3"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <ConsentToggle
              checked={true}
              disabled
              onChange={() => {}}
              label="Strictly necessary"
              description="Essential for login, session management, and security. Cannot be disabled."
            />
            <ConsentToggle
              checked={functional}
              onChange={setFunctional}
              label="Functional"
              description="Remembers your theme, language, sidebar state, and other preferences."
            />
            <ConsentToggle
              checked={analytics}
              onChange={setAnalytics}
              label="Analytics"
              description="Helps us understand how you use CBOps so we can improve the product."
            />
            <ConsentToggle
              checked={marketing}
              onChange={setMarketing}
              label="Marketing"
              description="Used for lead attribution and targeted communications. Disabled by default for workplace software."
            />
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-2 px-4 pb-4 flex-wrap">
          <button
            onClick={() => setShowDetails(v => !v)}
            className="flex items-center gap-1 text-xs text-white/50 hover:text-white/80 transition-colors mr-auto"
          >
            {showDetails ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {showDetails ? 'Hide' : 'Manage preferences'}
          </button>

          <button
            onClick={() => handleChoice('accept_necessary')}
            className="text-xs px-3 py-1.5 rounded-lg border border-white/15 text-white/70 hover:bg-white/8 hover:text-white transition-colors"
          >
            Necessary only
          </button>

          {showDetails ? (
            <button
              onClick={() => handleChoice('custom')}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-teal-500 hover:bg-teal-400 text-white font-semibold transition-colors"
            >
              <Check size={12} /> Save preferences
            </button>
          ) : (
            <button
              onClick={() => handleChoice('accept_all')}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-teal-500 hover:bg-teal-400 text-white font-semibold transition-colors"
            >
              <Check size={12} /> Accept all
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
